import { buildValidationKey, BusinessObjectBase, EventBusBase, L10nBase, LocalContext, ModelBase, RepositoryBase, RepositoryResponseCode, RepositorySaveResponse, Weaver } from '@skylab-shared/core';

import { CommandBus, RootContextBase } from '@skylab-angular-shared/core';
import { DialogButtonResult, DialogContentComponentBase, DialogInsService, DialogResolution, DialogState, ToastService } from '@skylab-angular-shared/ui';

import { inject } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { FormSaveCommand } from '../command/form-save.command';
import { ViewValidation, ViewValidationService } from '../form/form-validation';

export interface DictionaryDialogViewModelBase<TBO> {
    bo: TBO;
    isSaving: boolean;
    isFormReady: boolean;
    isFormSubmitted: boolean;
    showValidations: BehaviorSubject<boolean>
    isFormInEditMode: boolean;
}

export interface DictionaryDialogData<TID = number | string> {
    id?: TID;
}

export abstract class DictionaryDialogComponentBase
    <
    TModel extends ModelBase,
    TBO extends BusinessObjectBase<TModel>,
    TRepo extends RepositoryBase<TBO, TModel>,
    TViewModel extends DictionaryDialogViewModelBase<TBO>,
    TData extends DictionaryDialogData<any> = DictionaryDialogData,
    TResults extends DialogResolution = DialogResolution
    >
    extends DialogContentComponentBase<TViewModel, TData, TResults> {

    protected localContext: LocalContext;

    protected readonly viewValidationService = inject(ViewValidationService);
    public viewValidation!: ViewValidation;

    public constructor(
        protected repo: TRepo,
        protected toastService: ToastService,
        protected l10n: L10nBase,
        protected weaver: Weaver,
        rootContext: RootContextBase,
        protected commandBus: CommandBus,
        protected eventBus: EventBusBase,
        dialogService: DialogInsService,
    ) {
        super(dialogService);
        this.localContext = rootContext.createLocalContext();
    }

    protected override getInitialState(): TViewModel {
        return {
            bo: null as any,
            isSaving: false,
            isFormReady: false,
            isFormSubmitted: false,
            isFormInEditMode: this.data?.id != null,
            showValidations: new BehaviorSubject(this.data?.id != null)
        } as unknown as TViewModel;
    }

    protected override async prepareDataAsync(): Promise<void> {
        await this.preloadDataAsync();
        await this.initializeBusinessObjectAsync();
        await this.prepareViewModelAsync();

        this.initViewValidation();
        this.viewModel.bo.attachReadonlyChecker();
    }

    protected async initViewValidation(): Promise<void> {
        this.viewValidation = this.viewValidationService.create([this.viewModel.bo], this.viewModel.showValidations);
    }

    protected async preloadDataAsync(): Promise<void> {}

    protected async prepareViewModelAsync(): Promise<void> {}

    //#region BO
    protected async initializeBusinessObjectAsync(): Promise<void> {
        try {
            const bo = await this.provideBusinessObjectAsync();
            if (!bo)
                throw new Error('No business object found');

            this.viewModel.bo = bo;
        } catch (err) {
            this.logger.error(err);
            this.onCancel();
        }
    }

    public getValidationKey(propertyName: string, modelSun?: string): string | undefined {
        if (this.viewModel.bo && this.viewModel.bo.model) {
            const sun = modelSun || this.viewModel.bo.model.sun;
            return buildValidationKey(sun, propertyName);
        }
        return undefined;
    }

    protected async provideBusinessObjectAsync(): Promise<TBO> {
        try {
            const loadProperties = this.dictionaryToLoadProperties;
            if (loadProperties != null) {
                return await this.repo.loadAsync(loadProperties as any, this.localContext);
            } else {
                return await this.repo.createNewAsync(this.localContext);
            }
        } catch (err) {
            this.logger.error(err);
            throw err;
        }
    }

    protected get dictionaryToLoadProperties(): { [key: string]: string | number | null } | null {
        if (!this.data?.id) return null;
        return typeof this.data.id === 'string'
            ? { externalId: this.data.id }
            : { id: +this.data.id };
    }

    //#endregion

    //#region viewModel

    protected override isReady(): void {
        this.viewModel.isFormReady = true;
    }

    //#endregion

    //#region actions

    public async onConfirmAsync(): Promise<void> {
        try {
            this.viewModel.isFormSubmitted = this.viewModel.isSaving = true;
            this.viewModel.showValidations.next(true);
            const saveCommand = new FormSaveCommand<TRepo, TBO, TModel>({
                repo: this.repo,
                bo: this.viewModel.bo,
                canSaveAsync: this.canSaveAsync.bind(this)
            });
            const response = await this.commandBus.commandAsync(saveCommand);
            this.onSaveAsync(response);
        } catch (err) {
            this.logger.error(err);
        } finally {
            this.viewModel.isSaving = false;
        }
    }

    public override onAction(data: DialogButtonResult): void {
        if (data.state === DialogState.Confirm) {
            this.onConfirmAsync();
        }
        if (data.state === DialogState.Decline ||
            data.state === DialogState.Cancel) {
            this.onCancel();
        }
    }

    //#endregion

    //#region save

    public async canSaveAsync(): Promise<boolean> {
        return !this.isCanceled;
    }

    protected async onSaveAsync(response: RepositorySaveResponse): Promise<void> {
        if (response.code === RepositoryResponseCode.Success) {
            this.onSaveSuccess(response);
        } else if (response.code === RepositoryResponseCode.OutOfDateData) {
            this.onSaveOutdated();
        } else {
            this.onSaveError(response);
        }
    }

    protected onSaveSuccess(response?: RepositorySaveResponse): void {
        this.dialogService.close({ state: DialogState.Confirm, data: response?.object });
    }

    protected async onSaveOutdated(): Promise<void> {
        let closeToast: () => void;

        closeToast = await this.toastService.showAsync({
            type: 'error',
            text: this.l10n._T('scenes.shared.toast.form-state-context.data-has-changed-error.message'),
            duration: undefined,
            actions: [
                {
                    text: this.l10n._T('scenes.shared.toast.form-state-context.data-has-changed-error.buttons.reload'),
                    handler: () => {
                        this.onReload();
                        closeToast();
                    }
                },
                {
                    text: this.l10n._T('scenes.shared.toast.form-state-context.data-has-changed-error.buttons.do-nothing'),
                    handler: () => closeToast()
                }
            ]
        });
    }

    protected onReload(): void {
        this.dialogService.close({ state: DialogState.Confirm, data: RepositoryResponseCode.OutOfDateData });
    }

    protected onSaveError(response?: RepositorySaveResponse): void { }

    //#endregion

    public override dispose(): void {
        super.dispose();
        this.viewValidation?.dispose();

        if (this.viewModel.bo) {
            this.viewModel.bo.dispose();
        }
        if (this.localContext) {
            this.localContext.dispose();
        }
        this.deregisterAllListeners();
    }

}
